import {useState, useEffect, RefObject} from 'react'

/**
 * Loads a video component when in proximity to be displayed in the viewport
 *
 * @param {RefObject<HTMLElement>} ref - The ref for the video object to be observed
 * @returns {boolean} - Returns true if video is 1.5*viewport height from bottom of viewport
 */

const useScrollToLoadVideo = (ref: RefObject<HTMLElement>) => {
    const [isReady, setIsReady] = useState(false)

    useEffect(() => {
        if (typeof window === 'undefined') return

        let observer: IntersectionObserver | null = null

        const checkProximity = () => {
            setIsReady((prev) => {
                if (prev) return true
                const element = ref.current
                if (!element) return prev

                const rect = element.getBoundingClientRect()
                const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
                const extendedRange = viewportHeight * 2.5 // mirrors rootMargin-based distance
                const withinExtendedViewport = rect.top <= extendedRange && rect.bottom >= -extendedRange

                return withinExtendedViewport || prev
            })
        }

        if ('IntersectionObserver' in window) {
            const options = {
                rootMargin: `0px 0px ${1.5 * window.innerHeight}px 0px`,
                threshold: 0,
            }

            const observerCallback = ([entry]: IntersectionObserverEntry[]) => {
                if (entry.isIntersecting) {
                    setIsReady(true)
                }
            }

            observer = new IntersectionObserver(observerCallback, options)
            if (ref.current) {
                observer.observe(ref.current)
            }
        } else {
            // If the API is missing (very old Safari), mark as ready via fallback
            setIsReady(true)
        }

        // Run once on mount so videos already in view are marked ready immediately
        checkProximity()
        window.addEventListener('scroll', checkProximity, {passive: true})
        window.addEventListener('resize', checkProximity)

        return () => {
            observer?.disconnect()
            window.removeEventListener('scroll', checkProximity)
            window.removeEventListener('resize', checkProximity)
        }
    }, [ref])

    useEffect(() => {
        if (isReady) {
            const videoElement = ref.current?.querySelector('video') as HTMLVideoElement | null
            if (videoElement?.querySelector('source')) {
                videoElement.load()
            }
        }
    }, [ref, isReady])

    return isReady
}

export {useScrollToLoadVideo}
